import { withTransaction } from '@elastic/apm-rum-react'
import { Campaign, Environment, LogService, UserInDetails, onEnvironmentChange } from '@hub/shared'
import { SqBorderWithIcon, SqCallout, SqCardToCopy } from '@squidit/react-css'
import ContentByProfileComponent from '@src/components/content-by-profile/content-by-profile.component'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SubscriptionLike } from 'rxjs'
import DosDontsCampaignComponent from '../../components/dos-donts/dos-donts-campaign.component'
import ContentListComponent from './components/content-list-component/content-list-component.component'
import ReferralsLinkComponent from './components/referrals-link/referrals-link.component'
import ScheduleItemsComponent from './components/schedule-items/schedule-items.component'
import './orientation-content.page.scoped.scss'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  campaign: Campaign
  userInCampaign?: UserInDetails
}

const OrientationContentPageComponent = ({ campaign, userInCampaign }: Props) => {
  const [t] = useTranslation('campaignContentOrientation')
  const logService = useMemo(() => new LogService(), [])

  const onEnvironmentChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const [urlCreators, setUrlCreators] = useState<string>('')

  const tcmCampaignLink = userInCampaign.tcmCampaignLink ?? campaign?.tcmCampaignLink
  const tcmCampaignCode = userInCampaign.tcmCampaignCode ?? campaign?.tcmCampaignCode

  logService.init('Campaign Content Orientation Page')

  useEffect(() => {
    onEnvironmentChangeSubscription.current = onEnvironmentChange.subscribe((environments: Environment) => {
      setUrlCreators(environments?.squidProducts?.creators)
    })
    return () => onEnvironmentChangeSubscription?.current?.unsubscribe()
  }, [])

  return (
    <section>
      <div>
        {campaign?.activation?.brandUsername ? (
          <SqCardToCopy className="border-botom-card py-3" title={t('markProfile')} content={`@${campaign?.activation?.brandUsername}`} />
        ) : null}
        {campaign?.activation?.monitoringTag ? (
          <SqCardToCopy className="border-botom-card py-3" title={t('hashtag')} content={`#${campaign?.activation?.monitoringTag}`} />
        ) : null}
        <DosDontsCampaignComponent campaign={campaign}></DosDontsCampaignComponent>
        <ReferralsLinkComponent campaign={campaign} userInCampaign={userInCampaign}></ReferralsLinkComponent>
        {campaign?.socialNetworks?.includes('tiktok') && (tcmCampaignLink || tcmCampaignCode) ? (
          <div>
            {tcmCampaignLink ? (
              <SqCardToCopy
                className="border-botom-card py-3"
                title={t('brandedContentLinkLabel')}
                content={
                  <a style={{ color: 'var(--blue)' }} href={tcmCampaignLink} target={'_blank'}>
                    {tcmCampaignLink}
                  </a>
                }
              />
            ) : null}

            <SqCallout smallIconStyle icon="fa-solid fa-triangle-exclamation mr-2 sm yellow" title={t('required')}>
              <div className="display-flex-column">
                <p dangerouslySetInnerHTML={{ __html: t('seeHowTTCM') }} />
                <a
                  style={{ color: 'var(--blue)' }}
                  href={`${urlCreators}/docs/como-vincular-o-seu-video-retroativo-ao-tiktok-creator-marketplace-ttcm`}
                  target={'_blank'}
                >
                  {t('seeTutorial')}
                </a>
              </div>
            </SqCallout>
          </div>
        ) : null}
      </div>
      {(userInCampaign?.scope?.requiredContent && Object?.values(userInCampaign?.scope?.requiredContent)?.some((value) => !!value)) ||
      (userInCampaign?.scope?.suggestedContent && Object?.values(userInCampaign?.scope?.suggestedContent)?.some((value) => !!value)) ||
      !userInCampaign?.scope ? (
        <hr />
      ) : null}
      {userInCampaign?.scope && (
        <ContentByProfileComponent
          title={t('content')}
          scope={userInCampaign?.scope}
          className="p-0"
          style={{ backgroundColor: 'var(--background_secondary)', borderTop: 'none' }}
        />
      )}
      <ContentListComponent campaign={campaign}></ContentListComponent>

      <hr />

      <ScheduleItemsComponent campaign={campaign} userInCampaign={userInCampaign}></ScheduleItemsComponent>

      {campaign?.socialNetworks?.includes('instagram') ? (
        <SqBorderWithIcon iconColor="var(--gold_light)" title={t('important')}>
          {t('orientationPublicationsText')}{' '}
          <a style={{ color: 'var(--blue)' }} href={`${urlCreators}/docs/captura-autom%C3%A1tica-de-conte%C3%BAdo`} target={'_blank'}>
            {t('consult')}.
          </a>
        </SqBorderWithIcon>
      ) : null}
    </section>
  )
}

export default withTransaction('OrientationContentPageComponent', 'component')(OrientationContentPageComponent)
